import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownItemContent,
  IconButton,
  Tag,
} from '@ads/components-react';
import { ArrowUpRightIcon, EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import type { LibrarySlide } from '../../../store/atoms/slideLibraryAtom';
import { isSlideComplete } from '../../../utils/isSlideComplete';

export interface SlideCardProps {
  readonly slide: LibrarySlide;
  readonly categoryName: string;
  readonly onClick: () => void;
  /** Label do status obtido via useSlideStatuses. Exibido como Tag quando presente. */
  readonly statusLabel?: string;
  /** Variante visual da Tag de status. */
  readonly statusTagVariant: 'helper' | 'positive';
  /** Callback para alterar o status. Presente apenas quando slide.langId está disponível. */
  readonly onStatusChange: (newStatus: 1 | 2) => void;
  /** Callback para solicitar exclusão. Presente apenas quando slide.serverId está disponível. */
  readonly onDelete: () => void;
  /** Indica que a mutation de status está em andamento. */
  readonly isUpdatingStatus?: boolean;
  /** Indica que a mutation de exclusão está em andamento para este card. */
  readonly isDeleting?: boolean;
}

/**
 * Card de slide para o grid da biblioteca.
 * Exibe thumbnail, título, tags de categoria e status, além de ações (alterar status e excluir).
 */
export function SlideCard({
  slide,
  categoryName,
  onClick,
  statusLabel,
  statusTagVariant = 'helper',
  onStatusChange,
  onDelete,
  isUpdatingStatus = false,
  isDeleting = false,
}: SlideCardProps) {
  const { t } = useTranslation('slideEditor');

  /** Publicar só é permitido quando todas as 6 variantes (3 idiomas × 2 viewports) têm conteúdo. */
  const canPublish = isSlideComplete(
    slide.ssoImageLangs as unknown as Parameters<typeof isSlideComplete>[0],
  );
  /** Item de status: desabilitado ao tentar publicar sem variantes completas. */
  const isPublishDisabled = slide.status === 1 && canPublish;

  return (
    <div className="relative flex h-[16rem] flex-col gap-nano rounded-md border-hairline border-ctx-layout-border bg-ctx-layout-surface p-mini shadow-level1 transition-shadow hover:shadow-level2">
      <div className="flex w-full items-center justify-between gap-xxs">
        <div className="flex flex-wrap gap-quark">
          <Tag custom={{ text: 'text-nowrap' }} label={categoryName} size="sm" status="default" />
          {statusLabel !== undefined && statusLabel !== '' && (
            <Tag
              custom={{ text: 'text-nowrap' }}
              label={statusLabel}
              size="sm"
              status={statusTagVariant}
            />
          )}
        </div>
        {/* Action buttons — only shown when server-side IDs are available */}
        <Dropdown
          trigger={
            <IconButton
              icon={EllipsisVerticalIcon}
              hierarchy="tertiary"
              size="sm"
              ariaLabel={t('library.slide.actions')}
              isLoading={isUpdatingStatus}
            />
          }
          align="end"
        >
          <DropdownItem
            disabled={isPublishDisabled}
            onClick={() => {
              onStatusChange(slide.status === 1 ? 2 : 1);
            }}
          >
            <DropdownItemContent>
              {slide.status === 2 ? t('library.slide.setDraft') : t('library.slide.publish')}
            </DropdownItemContent>
          </DropdownItem>
          <DropdownItem status="destructive" disabled={isDeleting} onClick={() => onDelete()}>
            <DropdownItemContent>{t('library.slide.delete')}</DropdownItemContent>
          </DropdownItem>
        </Dropdown>
      </div>

      <div className="flex h-full flex-col justify-between gap-nano px-quark pb-quark">
        <span className="line-clamp-3 text-ctx-content-title ts-heading-sm">
          {slide.title ?? t('library.slide.untitled')}
        </span>

        <span className="text-ctx-content-title ts-caption-xxxs">
          {dayjs(slide.dateStart).format('DD/MM/YYYY')} -{' '}
          {dayjs(slide.dateEnd).format('DD/MM/YYYY')}
        </span>
      </div>
      <Button
        trailingIcon={ArrowUpRightIcon}
        className="mt-auto"
        fullWidth
        onClick={onClick}
        hierarchy="secondary"
      >
        Acessar
      </Button>
    </div>
  );
}
